import {
    RiCalendar2Line,
    RiDeleteBinLine,
    RiEdit2Line,
    RiFlashlightLine,
    RiLayout6Line,
} from "@remixicon/react";
import React, { useCallback, useMemo, useState } from "react";
import {
    ActionExecuteStartCondition,
    FormType,
    GetFormDataDocument,
    GetFormDocument,
    GetRequestTypesDocument,
    GetSettingsDocument,
    GetWorkflowsDocument,
    Settings,
    useAttachWorkflowMutation,
    useCloneFormMutation,
    useDeleteWorkflowMutation,
    useGetWorkflowsQuery,
    useSaveFormMutation,
    useSaveSettingsMutation,
    useSaveWorkflowMutation,
    Workflow,
    WorkflowTriggerType,
} from "../../../../__generated__/api";
import SettingsContainer from "../../../../components/theme/settings/SettingsContainer";

import useDeleteConfirm from "../../../../components/confirm/Delete";
import DateRangeCalendarPicker from "../../../../components/pickers/date-range/DateRangeCalendarPicker";
import Badge from "../../../../components/theme/badge/Badge";
import createFilterList from "../../../../components/theme/filter-list/FilterList";
import createTableContainer from "../../../../components/theme/table";
import Time from "../../../../components/time/Time";
import useRouting, { useParameterFilters } from "../../../../contexts/Routing";
import { isDateInRange, parseFilterDates, tryParseDate } from "../../../../date";
import CreateAutomation from "./CreateAutomation";
import Template from "../../../../components/template/Template";
import { ICON_MAP } from "../request-types/RequestTypesTable";
import { ARTICLES } from "../../../../components/help/Help";
import { ROLE_MAP } from "../../../../job-function-roles";
import WORKFLOW_TRIGGER_TYPES from "./WorkflowTriggerTypes";
import { removeTypenameList } from "../../../../api";
import usePortal from "../../../../contexts/PortalContext";
import useCurrentSession from "../../../../contexts/session";
import { AutomationTab } from "../../../../components/workflow-v3/providers/WorkflowModalProvider";
import ViewAutomation from "./ViewAutomation";

const TableContainer = createTableContainer<Workflow>();

const FilterList = createFilterList<WorkflowTriggerType | "all">();

const WORKFLOW_TRIGGER_FORM_TYPE_MAP: Partial<Record<WorkflowTriggerType, FormType>> = {
    [WorkflowTriggerType.InboundWebhook]: FormType.InboundWebhook,
    [WorkflowTriggerType.RequestType]: FormType.Default,
    [WorkflowTriggerType.Action]: FormType.Action,
    [WorkflowTriggerType.Email]: FormType.Action,
    [WorkflowTriggerType.WebChat]: FormType.Action,
    [WorkflowTriggerType.WorkspaceAction]: FormType.Action,
};

export default function ActionsV3({ settings }: { settings?: Settings }) {
    const { data, error, loading: isLoading } = useGetWorkflowsQuery();

    const [cloneForm] = useCloneFormMutation();
    const [saveWorkflow] = useSaveWorkflowMutation();
    const [saveSettings] = useSaveSettingsMutation();
    const [attachWorkflow] = useAttachWorkflowMutation({
        refetchQueries: [
            { query: GetWorkflowsDocument },
            { query: GetSettingsDocument },
        ],
    });
    const [saveForm] = useSaveFormMutation({
        refetchQueries: result => [
            { query: GetFormDataDocument },
            {
                query: GetFormDocument,
                variables: {
                    id: result.data?.saveForm?.id,
                    hasId: true,
                },
            },
        ],
    });

    const { id: portalId } = usePortal();

    const { historyPush, Paths } = useRouting();

    const [filters, setFilters] = useParameterFilters(Paths.ADMINISTRATOR_SETTING, {
        source: "all",
    });

    const [deleteWorkflow] = useDeleteWorkflowMutation();

    const userSession = useCurrentSession();
    const [viewWorkflowId, setViewWorkflowId] = useState<string | undefined>(undefined);
    const [form, setForm] = useState<{ formId: string | undefined; formType: WorkflowTriggerType } | undefined>(undefined);

    const { element: deleteElement, setDeleting } = useDeleteConfirm<Workflow>(
        async group => {
            await deleteWorkflow({
                refetchQueries: [
                    { query: GetWorkflowsDocument },
                ],
                variables: {
                    id: group.id,
                },
            });
        },
        user => user.name,
        "Delete Automation",
        "Do you want to delete",
        "Remove",
    );

    const { displayItems, groupOptions } = useMemo(
        () => ({
            displayItems: data?.workflows?.filter(row => !filters.source
                || filters.source === "all"
                || row.triggers?.some(trigger => trigger.type === filters.source)),
            groupOptions: data?.workflows?.distribution(trigger => trigger.triggers?.firstElement?.type as string) ?? {},
        }),
        [filters.source, data?.workflows],
    );

    const [isTemplateOpen, setIsTemplateOpen] = useState(false);

    const { data: templatesData } = useGetWorkflowsQuery();
    const templates = useMemo(() => templatesData?.templates.map(template => {
        const JobIcon = template.jobFunctions?.isNotEmpty ? ROLE_MAP[template.jobFunctions?.firstElement]?.icon : undefined;
        return ({
            id: template.id,
            title: template.name,
            description: template.description ?? "",
            categories: template.jobFunctions?.mapFilter(jobFunction => ROLE_MAP[jobFunction]?.title) ?? [],
            icon: JobIcon ? <JobIcon /> : undefined,
        });
    }) ?? [], [templatesData?.templates]);

    const handleSelectAutomation = useCallback(async (type: WorkflowTriggerType) => {
        const typeLabel = WORKFLOW_TRIGGER_TYPES[type].text;
        const newWorkflow = await saveWorkflow({
            variables: {
                workflow: {
                    name: `New ${typeLabel} Automation`,
                    description: "",
                    steps: [],
                },
            },
        });

        const newWorkflowId = newWorkflow.data?.saveWorkflow?.id;

        const triggerType = WORKFLOW_TRIGGER_TYPES[type];

        const defaultName = `${triggerType.text}`;
        const defaultDescription = triggerType.description;
        const formType = WORKFLOW_TRIGGER_FORM_TYPE_MAP[type];

        if (type === WorkflowTriggerType.Emoji) {
            if (!settings) {
                return;
            }

            const newEmojiActions = removeTypenameList(settings.channelSlack?.emojiActions ?? [])
                .concat({
                    allowedExecuteGroups: [],
                    workflowId: newWorkflowId,
                    emoji: "placeholder_emoji",
                })
                .dedupe(action => action.emoji);

            await saveSettings({
                variables: {
                    channelSlack: {
                        emojiActions: newEmojiActions,
                    },
                },
            });

            const attachedWorkflow = await attachWorkflow({
                variables: {
                    id: newWorkflowId,
                    triggers: [{
                        referenceId: portalId,
                        text: "Global",
                        type,
                    }],
                },
            });

            setViewWorkflowId(attachedWorkflow.data?.attachWorkflow?.id);
            setForm({
                formId: undefined,
                formType: type,
            });

            return;
        }

        if (type === WorkflowTriggerType.ChannelTag) {
            const attachedWorkflow = await attachWorkflow({
                variables: {
                    id: newWorkflowId,
                    triggers: [{
                        referenceId: portalId,
                        text: "Global",
                        type,
                    }],
                },
            });

            setViewWorkflowId(attachedWorkflow.data?.attachWorkflow?.id);
            setForm({
                formId: undefined,
                formType: type,
            });
            return;
        }

        const saved = await saveForm({
            variables: {
                form: {
                    name: defaultName,
                    type: formType,
                    description: defaultDescription,
                    fields: [],
                    workflowId: newWorkflow.data?.saveWorkflow?.id,
                    actionSettings: {
                        startCondition: ActionExecuteStartCondition.AfterStarted,
                    },
                    deflection: {
                        isEnabled: false,
                    },
                    isCreatePrivate: false,
                    userGroupIds: userSession.user?.userGroups?.toIdList("id") ?? [],
                },
            },
        });

        const formId = saved.data?.saveForm?.id;
        const formName = saved.data?.saveForm?.name ?? "";

        const attachedWorkflow = await attachWorkflow({
            variables: {
                id: newWorkflowId,
                triggers: [{
                    referenceId: formId,
                    text: formName,
                    type,
                }],
            },
        });

        setViewWorkflowId(attachedWorkflow.data?.attachWorkflow?.id);
        setForm({
            formId,
            formType: type,
        });

        if (type === WorkflowTriggerType.RequestType) {
            historyPush(Paths.ADMINISTRATOR_SETTING_ID, {
                setting: "request-forms",
                id: formId,
            });
        }
    }, [settings]);

    return (
        <SettingsContainer navigation="none">
            <SettingsContainer.Section title="Automations" shouldDisplayTitle={false}>
                <SettingsContainer.Setting
                    title="Automations"
                    description="Manage the automations users see in their dashboards."
                    permissions={SettingsContainer.Setting.Permissions.Administrator}
                    isFullWidth
                >
                    <TableContainer data={displayItems as Workflow[] ?? []} isLoading={isLoading} error={error}>
                        <TableContainer.Empty>
                            <TableContainer.Empty.Default
                                text="Automations are used to automate tasks in your workflows."
                                title="Automations"
                                icon={<RiFlashlightLine />}
                                onClick={() => setIsTemplateOpen(true)}
                                buttonText="Add Automation"
                            />
                        </TableContainer.Empty>
                        <TableContainer.Header>
                            <FilterList
                                selectedItem={filters.source as WorkflowTriggerType ?? "all"}
                                onChangeSelected={key => setFilters("source", key)}
                            >
                                <FilterList.Button filterItem="all" text="All" count={data?.workflows?.length} />
                                {Object.entries(groupOptions).map(([type, count]) => {
                                    const source = WORKFLOW_TRIGGER_TYPES[type as WorkflowTriggerType];
                                    return source ? (
                                        <FilterList.Button
                                            filterItem={type as WorkflowTriggerType}
                                            text={source.text}
                                            count={count}
                                            key={type}
                                            // prefixIcon={source.icon}
                                        />
                                    ) : undefined;
                                })}
                            </FilterList>
                        </TableContainer.Header>
                        <TableContainer.TableControls>

                            <TableContainer.TableControls.Search
                                search={(query, group) => group.name?.toLocaleLowerCase().includes(query)
                                    || group.description?.toLocaleLowerCase().includes(query)
                                    || group.triggers?.some(row => row.type.toLocaleLowerCase().includes(query)
                                        || row.text?.toLocaleLowerCase().includes(query))}
                            />
                            <TableContainer.TableControls.Seperator />

                            <TableContainer.TableControls.Filter
                                defaultValue={{
                                    startDate: undefined as Date | undefined,
                                    endDate: undefined as Date | undefined,
                                }}
                                deserialize={keys => (typeof keys === "object" && !Array.isArray(keys) ? ({
                                    startDate: keys.startDate ? new Date(keys.startDate) : undefined,
                                    endDate: keys.endDate ? new Date(keys.endDate) : undefined,
                                }) : undefined)}
                                serialize={value => ({
                                    startDate: value.startDate?.toISOString(),
                                    endDate: value.endDate?.toISOString(),
                                })}
                                filterKey="modifiedDate"
                                onFilter={(item, currentValue) => isDateInRange(tryParseDate(item.modifiedDate), currentValue)}
                            >
                                {(currentValue, setValue) => (
                                    <DateRangeCalendarPicker
                                        toggleText="Modified"
                                        icon={<RiCalendar2Line />}
                                        value={{
                                            startDate: currentValue.startDate,
                                            endDate: currentValue.endDate,
                                        }}
                                        onChange={change => {
                                            const dates = parseFilterDates(change);
                                            setValue({
                                                startDate: dates.startDate ? new Date(dates.startDate) : undefined,
                                                endDate: dates.endDate ? new Date(dates.endDate) : undefined,
                                            });
                                        }}
                                    />
                                )}

                            </TableContainer.TableControls.Filter>
                            <TableContainer.TableControls.Filters shouldWriteUrl />

                            <TableContainer.TableControls.SortBy />
                            <TableContainer.TableControls.Create
                                text="Add Automation"
                                onClick={() => setIsTemplateOpen(true)}
                            />
                        </TableContainer.TableControls>
                        <TableContainer.Table>
                            <TableContainer.TableValue
                                columnKey="name"
                                title="Name"
                                sortBy={item => item.name?.toLowerCase()}
                            >
                                {item => item.name}
                            </TableContainer.TableValue>
                            <TableContainer.TableValue
                                columnKey="description"
                                title="Description"
                                sortBy={item => item.description?.toLowerCase()}
                            >
                                {item => item.description}
                            </TableContainer.TableValue>
                            <TableContainer.TableValue
                                columnKey="triggerType"
                                title="Type"
                                sortBy={item => item.triggers?.firstElement?.type}
                            >
                                {item => WORKFLOW_TRIGGER_TYPES[item.triggers?.firstElement?.type as WorkflowTriggerType]?.badge}
                            </TableContainer.TableValue>
                            <TableContainer.TableValue
                                columnKey="linkedTo"
                                title="Linked To"
                                sortBy={item => item.triggers?.firstElement?.text}
                            >
                                {item => <Badge variant="primary">{item.triggers?.firstElement?.text}</Badge>}
                            </TableContainer.TableValue>
                            <TableContainer.TableValue
                                columnKey="modified"
                                title="Last Modified"
                                sortBy={item => item.modifiedDate?.toLocaleString()}
                            >
                                {item => <Time date={item.modifiedDate} variant="date-time" />}
                            </TableContainer.TableValue>

                            <TableContainer.Action
                                icon={<RiEdit2Line />}
                                altText="Edit Automation"
                                onClick={workflowItem => {
                                    setViewWorkflowId(workflowItem.id);

                                    const trigger = workflowItem.triggers?.firstElement;
                                    const formType = trigger?.type;

                                    let formId = trigger?.referenceId;

                                    if (
                                        formType === WorkflowTriggerType.ChannelTag
                                        || formType === WorkflowTriggerType.Emoji
                                        || formType === WorkflowTriggerType.WorkspaceAction
                                    ) {
                                        formId = undefined;
                                    }

                                    if (formType === WorkflowTriggerType.RequestType) {
                                        historyPush(Paths.ADMINISTRATOR_SETTING_ID, {
                                            setting: "request-forms",
                                            id: formId,
                                        });
                                    } else if (formType) {
                                        setForm({
                                            formId,
                                            formType,
                                        });
                                    }
                                }}
                            />
                            <TableContainer.Action
                                icon={<RiDeleteBinLine />}
                                altText="Delete Automation"
                                onClick={row => setDeleting(row)}
                            />

                        </TableContainer.Table>
                    </TableContainer>
                    {deleteElement}
                    <Template
                        templates={templates}
                        title="Add Automation"
                        helpText="How to Create an Automation"
                        categoryIcons={ICON_MAP}
                        categoryIcon={<RiLayout6Line />}
                        helpArticleId={ARTICLES.AUTOMATION_CREATE}
                        shouldShow={isTemplateOpen}
                        onHide={() => setIsTemplateOpen(false)}
                        onUseTemplate={async template => {
                            await cloneForm({
                                refetchQueries: [GetRequestTypesDocument],
                                variables: {
                                    id: template.id,
                                },
                            });
                        }}
                        createScreen={(
                            <CreateAutomation
                                onSelect={async type => {
                                    await handleSelectAutomation(type);
                                    setIsTemplateOpen(false);
                                }}
                            />
                        )}
                    />

                    {
                        viewWorkflowId && (
                            <ViewAutomation
                                settings={settings}
                                workflowId={viewWorkflowId}
                                form={form}
                                onHide={() => {
                                    setViewWorkflowId(undefined);
                                    setForm(undefined);
                                }}
                                startTab={AutomationTab.GENERAL_DETAILS}
                            />
                        )
                    }
                </SettingsContainer.Setting>
            </SettingsContainer.Section>
        </SettingsContainer>

    );
}

ActionsV3.defaultProps = {
    settings: undefined,
};
